import { Container } from 'Components/Container';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { deleteGapAnalysisVinculo } from '../../../services/gap-analysis';
import { getComplianceProgramDetails } from '../../../services/complice-program'
import { PageLoaderShow } from '../../../Components/PageLoader/Show';
import { ExpandableTable } from '../../../Components/ExpandableTable';
import { tableHeader } from './table-header';
import { tableEntity } from './table-entity';
import { Information } from '../../../Components/Information';
import { StatusProgressBar  } from '../../../Components/StatusProgressBar';
import { Card } from '../../../Components/Card';
import { Alert, Box, Link, Snackbar } from '@mui/material';
import { Title } from '../../GapAnalysis/Show/ControlDetal/styles';
import { ButtonAdd } from '../../../Components/Container/styles';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Column, Row } from '../../../Components/Grid';
import { ModalConfirmDelete } from '../../../Components/ModalConfirmDelete';
import { typeLabelKeys, statusControl, maturityLevelKeys, translateDomain } from '../../../utils/labels_utils'
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../store/auth';
import { PageHelper } from '../../../Components/PageHelper';
import { useTranslation } from 'react-i18next';


export const ComplianceProgramShow = () => {
  const { t } = useTranslation(['company', 'gap-analysis', 'common']);
  const descriptionContent = (
    <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "4px" }}>
      {Object.entries(statusControl).map(([key, { labelKey, color, descriptionKey }]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <div style={{ width: "16px", height: "16px", backgroundColor: color, borderRadius: "4px" }}></div>
          <strong>{t(labelKey)}</strong> - {t(descriptionKey)}
        </div>
      ))}
    </div>
  );

  const tourSteps = [
    { target: 'body', content: t('company:complianceProgram.show.tour.step0'), placement: 'center', disableBeacon: true },
    { target: '[data-tour="page-title"]', content: t('company:complianceProgram.show.tour.step1'), disableScrolling: true },
    { target: '[data-tour="vincular-button"]', content: t('company:complianceProgram.show.tour.step2'), disableScrolling: true },
    { target: '[data-tour="status-bar"]', content: t('company:complianceProgram.show.tour.step3'), disableScrolling: true },
    { target: '[data-tour="status-info"]', content: t('company:complianceProgram.show.tour.step4'), disableScrolling: true },
    { target: '[data-tour="filters"]', content: t('company:complianceProgram.show.tour.step5') },
    { target: '[data-tour="controls-table"]', content: t('company:complianceProgram.show.tour.step6') },
    { target: 'body', content: t('company:complianceProgram.show.tour.step7'), placement: 'center' },
    { target: 'body', content: t('company:complianceProgram.show.tour.step8'), placement: 'center' },
  ];
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [filters, setFilters] = useState({
    peso: '',
    dominio: '',
    vinculo: '',
    entidadeTipo: '',
    entidadeNome: '',
    status: '',
  });

  const {
    isLoading,
    error,
    refetch,
    data: gapAnalysis,
  } = useQuery('getComplianceProgramDetails', async () => {
    try {
      const data = await getComplianceProgramDetails(selectedTenant._id);
      return data.status === 200 ? data.data : null;
    } catch (err) {
      if (err?.response?.status === 404) {
        return { hasComplianceProgram: false };
      }
      throw err;
    }
  });

  if (isLoading) return <p>{t('company:complianceProgram.loading')}</p>;
  if (error) return <p>{t('company:complianceProgram.loadingError')}</p>;

  if (gapAnalysis?.hasComplianceProgram === false) {
    return (
      <Information
        title={t('company:complianceProgram.notFound.title')}
        description={
          <>
            <div>{t('company:complianceProgram.notFound.description')}</div>
            <div>
              <Link
                href="/compliance-program/add"
                style={{ color: '#007bff', textDecoration: 'underline' }}
              >
                {t('company:complianceProgram.notFound.link')}
              </Link>
            </div>
          </>
        }
        style={{
          marginTop: '10px',
          marginBottom: '10px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '8px',
          padding: '16px',
          color: '#856404',
        }}
      />
    );
  }


  const id = gapAnalysis.gapAnalysis?._id;

  const formattedStatuses = Object.keys(statusControl).map(status => ({
    label: t(statusControl[status].labelKey),
    count: gapAnalysis.statusCounts[status] || 0,
    color: statusControl[status].color,
  }));


  const uniqueValues = (key) => [...new Set(gapAnalysis.controls.map(control => control[key]))].sort();

  const getUniqueEntities = () => {
    const entityMap = new Map();
    const entityTypes = new Set();
    const entityStatuses = new Set();
    const maturityLevels = new Set();

    gapAnalysis.controls.forEach(control => {
      control.entities.forEach(entity => {
        const key = `${entity.entity_id}-${entity.entity_type}`;

        if (!entityMap.has(key)) {
          entityMap.set(key, {
            entity_id: entity.entity_id,
            entity_name: entity.entity_name,
          });
        }

        entityTypes.add(entity.entity_type);
        entityStatuses.add(entity.status);
        maturityLevels.add(entity.maturity_level);

      });
    });

    return {
      entities: Array.from(entityMap.values()),
      entityTypes: Array.from(entityTypes),
      entityStatuses: Array.from(entityStatuses),
      maturityLevels: Array.from(maturityLevels),

    };
  };

  const { entities, entityTypes, entityStatuses, maturityLevels  } = getUniqueEntities();

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  const filteredControls = gapAnalysis.controls
    .map(control => {
      const filteredEntities = filters.entidadeNome || filters.entidadeTipo || filters.maturityLevel || filters.status
        ? control.entities.filter(entity => (
          (filters.entidadeTipo ? entity.entity_type === filters.entidadeTipo : true) &&
          (filters.entidadeNome ? entity.entity_id === filters.entidadeNome : true) &&
          (filters.maturityLevel ? entity.maturity_level === filters.maturityLevel : true) &&
          (filters.status ? entity.status === filters.status : true)
        ))
        : control.entities;

      return {
        ...control,
        entities: filteredEntities,
      };
    })
    .filter(control =>
      (control.entities.length > 0 || !(filters.entidadeNome || filters.entidadeTipo || filters.maturityLevel || filters.status)) &&
      (filters.peso ? parseInt(control.controlWeighting, 10) === parseInt(filters.peso, 10) : true) &&
      (filters.dominio ? control.scfDomain === filters.dominio : true) &&
      (filters.vinculo ? control.vinculado.toString() === filters.vinculo : true)
    );

  const link_vinculo = `/compliance-program/show/${id}`;

  const button = (
    <div data-tour="vincular-button">
      <ButtonAdd
        label={t('company:complianceProgram.show.linkControls')}
        className="no-margin"
        onClick={() => navigate(`${link_vinculo}/vincular`)}
      />
    </div>
  );

  const link_ = `compliance-program/${id}`;

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };
  const callDeleteAction = async (itemToDelete, onClose, refetch, setSnackbar,) => {
    try {
      await deleteGapAnalysisVinculo(itemToDelete._id);
      setSnackbar({ open: true, message: t('company:complianceProgram.show.delete.successMessage'), severity: "success" });
      refetch();
      onClose();
    } catch (error) {
      setSnackbar({ open: true, message: t('company:complianceProgram.show.delete.errorMessage'), severity: "error" });
    }
  };


  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setSnackbar);
    } catch (error) {
      onCloseDeleteModal();

      setSnackbar({
        open: true,
        message: error.response?.data?.message ?? t('company:complianceProgram.show.delete.errorMessage'),
        severity: "error",
      });
    }
  };


  return (
    <>
      {!isLoading && !error && (
        <>
        {/* Helper Interativo */}
        <PageHelper 
          steps={tourSteps}
          tourKey="compliance-program-show-tour"
          autoStart={true}
          showButton={true}
        />
        <Container title={`${gapAnalysis.gapAnalysis?.name}`}
                   breadcrumb={`${t('company:complianceProgram.show.breadcrumb')} / ${gapAnalysis.gapAnalysis?.name}`}
                   actionButton={button}
        >
          <Box sx={{ marginBottom: '10px', marginTop: '10px' }} data-tour="status-bar">
            <Card>
              <Title className="m-0 mb-2">{t('company:complianceProgram.show.statusTitle')}</Title>
              <StatusProgressBar statuses={formattedStatuses} />
            </Card>
          </Box>

          <div data-tour="status-info">
            <Information
              title={t('company:complianceProgram.show.statusInfo')}
              description={descriptionContent}
              style={{
                marginTop: '10px',
                marginBottom: '10px',
              }}
            />
          </div>


          <Card className="mb-2 mt-3" data-tour="filters">
            <Row className="mb-3" md={'3'}>
              <Column xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="dominio-label" shrink>{t('company:complianceProgram.show.filters.domain')}</InputLabel>
                  <Select
                    labelId="dominio-label"
                    variant="outlined"
                    label={t('company:complianceProgram.show.filters.domain')}
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.dominio} onChange={(e) => handleFilterChange("dominio", e.target.value)}>
                    <MenuItem value="">{t('company:complianceProgram.show.filters.all')}</MenuItem>
                    {uniqueValues('scfDomain').map(value => (
                      <MenuItem key={value} value={value}>{translateDomain(t, value)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="vinculo-label" shrink>{t('company:complianceProgram.show.filters.bond')}</InputLabel>
                  <Select
                    labelId="vinculo-label"
                    variant="outlined"
                    label={t('company:complianceProgram.show.filters.bond')}
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.vinculo} onChange={(e) => handleFilterChange("vinculo", e.target.value)}>
                    <MenuItem value="">{t('company:complianceProgram.show.filters.all')}</MenuItem>
                    <MenuItem value="true">{t('company:complianceProgram.show.filters.yes')}</MenuItem>
                    <MenuItem value="false">{t('company:complianceProgram.show.filters.no')}</MenuItem>
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel  id="status-label" shrink>{t('company:complianceProgram.show.filters.status')}</InputLabel>
                  <Select
                    labelId="status-label"
                    variant="outlined"
                    label={t('company:complianceProgram.show.filters.status')}
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.status} onChange={(e) => handleFilterChange("status", e.target.value)}>
                    <MenuItem value="">{t('company:complianceProgram.show.filters.all')}</MenuItem>
                    {entityStatuses.map(status => (
                      <MenuItem key={status} value={status}>{statusControl[status] ? t(statusControl[status].labelKey) : status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
            </Row>
            <Row className="mb-3" md={'3'}>
              <Column xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel  id="name-entity-label" shrink >{t('company:complianceProgram.show.filters.entityName')}</InputLabel>
                  <Select
                    labelId="name-entity-label"
                    label={t('company:complianceProgram.show.filters.entityName')}
                    variant="outlined"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.entidadeNome} onChange={(e) => handleFilterChange("entidadeNome", e.target.value)}>
                    <MenuItem value="">{t('company:complianceProgram.show.filters.allFeminine')}</MenuItem>
                    {entities.map((entity) => (
                      <MenuItem key={entity.entity_id} value={entity.entity_id}>
                        {entity.entity_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel  id="type-entity-label" shrink >{t('company:complianceProgram.show.filters.entityType')}</InputLabel>
                  <Select
                    labelId="type-entity-label"
                    label={t('company:complianceProgram.show.filters.entityType')}
                    variant="outlined"
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.entidadeTipo} onChange={(e) => handleFilterChange("entidadeTipo", e.target.value)}>
                    <MenuItem value="">{t('company:complianceProgram.show.filters.all')}</MenuItem>
                    {entityTypes.map(type => (
                      <MenuItem key={type} value={type}>{typeLabelKeys[type] ? t(typeLabelKeys[type]) : type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="maturity-type-label" shrink >{t('company:complianceProgram.show.filters.maturity')}</InputLabel>
                  <Select
                    labelId="maturity-type-label"
                    label={t('company:complianceProgram.show.filters.maturity')}
                    notched
                    variant="outlined"
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    value={filters.maturityLevel} onChange={(e) => handleFilterChange("maturityLevel", e.target.value)}>
                    <MenuItem value="">{t('company:complianceProgram.show.filters.all')}</MenuItem>
                    {maturityLevels.map(level => (
                      <MenuItem key={level} value={level}>{maturityLevelKeys[level] ? t(maturityLevelKeys[level]) : level}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
              <Column xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="peso-label"  shrink>{t('company:complianceProgram.show.filters.weight')}</InputLabel>
                  <Select
                    labelId="peso-label"
                    label={t('company:complianceProgram.show.filters.weight')}
                    notched
                    sx={{
                      minHeight: '40px',
                      maxHeight: '40px',
                      margin: '0 auto',
                      width: '100%',
                    }}
                    variant="outlined"
                    value={filters.peso} onChange={(e) => handleFilterChange("peso", e.target.value)}
                  >
                    <MenuItem value="">{t('company:complianceProgram.show.filters.all')}</MenuItem>
                    {uniqueValues('controlWeighting').map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Column>
            </Row>
          </Card>
          <div data-tour="controls-table">
            <ExpandableTable
              rows={filteredControls}
              columns={tableHeader(t)}
              link={link_}
              linkExtend="compliance-program/control"
              entityColumns={tableEntity(t)}
              enableSearch={true}
              enableSorting={true}
              title={t('company:complianceProgram.show.controls')}
              onClickDelete={onDelete}
            />
          </div>

          <ModalConfirmDelete
            description={t('company:complianceProgram.show.delete.confirm', { name: itemToDelete?.entity_name })}
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
        </>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>{t('company:complianceProgram.show.error')}</div>}
    </>
  );
};
